import type { AppleClient } from '@hewey/shared'
import { z } from 'zod'

// True only in the iOS build (`--mode ios`, client/.env.ios). That build asks
// for the authorization code through Apple's native sheet; the website uses
// Apple's JS popup.
//
// Defined here and not in config.ts: Rolldown only leaves the Capacitor chunk
// out of the website build when this constant sits in the same module as the
// dynamic import below.
const NATIVE = import.meta.env.VITE_NATIVE === 'true'

// Resolves to the module, not the plugin: see token.ts.
const plugin = () => import('@capgo/capacitor-social-login')

// Set in client/.env.production only. Apple won't redirect to localhost, so
// `npm run dev` leaves the button out.
const SERVICES_ID: string | undefined = import.meta.env.VITE_APPLE_SERVICES_ID
const REDIRECT_URI: string | undefined = import.meta.env.VITE_APPLE_REDIRECT_URI

export const APPLE_SIGN_IN = NATIVE || Boolean(SERVICES_ID && REDIRECT_URI)

// The plugin's code for a sheet the person backed out of.
const USER_CANCELLED = 'USER_CANCELLED'

const NativeResult = z.object({
	result: z.object({
		authorizationCode: z.string().min(1),
		// Only on the first authorization. The plugin sends '' when absent.
		profile: z.object({ givenName: z.string().nullish() }),
	}),
})

type AppleAuthorization = {
	code: string
	client: AppleClient
	givenName: string | null
}

const WEB_SCRIPT =
	'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'

// Apple's codes for a popup the person closed or cancelled.
const WEB_CANCELLED = new Set<unknown>([
	'popup_closed_by_user',
	'user_cancelled_authorize',
])

type AppleIdAuth = {
	init: (config: {
		clientId: string
		scope: string
		redirectURI: string
		usePopup: boolean
	}) => void
	signIn: () => Promise<unknown>
}

const WebResult = z.object({
	authorization: z.object({ code: z.string().min(1) }),
	// Only on the first authorization.
	user: z
		.object({
			name: z.object({ firstName: z.string().nullish() }).nullish(),
		})
		.nullish(),
})

let webReady: Promise<AppleIdAuth> | null = null

// Loads appleid.auth.js once; a failed load isn't cached, so the next call
// retries.
const loadWeb = (): Promise<AppleIdAuth> => {
	webReady ??= new Promise<AppleIdAuth>((resolve, reject) => {
		const script = document.createElement('script')
		script.src = WEB_SCRIPT
		script.addEventListener('load', () => {
			const auth = (window as { AppleID?: { auth: AppleIdAuth } }).AppleID
				?.auth
			if (!auth) {
				reject(new Error('appleid.auth.js loaded without AppleID'))
				return
			}
			auth.init({
				clientId: SERVICES_ID ?? '',
				scope: 'name email',
				redirectURI: REDIRECT_URI ?? '',
				usePopup: true,
			})
			resolve(auth)
		})
		script.addEventListener('error', () => {
			script.remove()
			reject(new Error('appleid.auth.js failed to load'))
		})
		document.head.append(script)
	}).catch((e: unknown) => {
		webReady = null
		throw e
	})
	return webReady
}

// Loads Apple's script before the tap. Safari only opens the popup while the
// tap is recent, and downloading the script first can take too long.
export const prepareAppleSignIn = (): void => {
	if (!NATIVE && APPLE_SIGN_IN) loadWeb().catch(() => {})
}

const signInWeb = async (): Promise<AppleAuthorization | null> => {
	const auth = await loadWeb()
	let raw: unknown
	try {
		raw = await auth.signIn()
	} catch (e) {
		if (WEB_CANCELLED.has((e as { error?: unknown }).error)) {
			return null
		}
		throw e
	}
	const { authorization, user } = WebResult.parse(raw)
	return {
		code: authorization.code,
		client: 'web',
		givenName: user?.name?.firstName || null,
	}
}

let initialized: Promise<void> | null = null

// Null when the person closes Apple's sheet or popup without signing in.
export const signInWithApple = async (): Promise<AppleAuthorization | null> => {
	if (!NATIVE) return signInWeb()
	const { SocialLogin } = await plugin()
	// An empty redirectUrl keeps the plugin from posting the code anywhere,
	// and useProperTokenExchange hands it back unspent for the server.
	initialized ??= SocialLogin.initialize({
		apple: { redirectUrl: '', useProperTokenExchange: true },
	}).catch((e: unknown) => {
		initialized = null
		throw e
	})
	await initialized

	let raw: unknown
	try {
		raw = await SocialLogin.login({
			provider: 'apple',
			options: { scopes: ['email', 'name'] },
		})
	} catch (e) {
		if ((e as { code?: unknown }).code === USER_CANCELLED) return null
		throw e
	}
	const { result } = NativeResult.parse(raw)
	return {
		code: result.authorizationCode,
		client: 'ios',
		givenName: result.profile.givenName || null,
	}
}
